import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import 'react-diff-view/style/index.css';
import '../index.css';
import { Diff, Hunk, parseDiff } from 'react-diff-view';
import ReactMarkdown from 'react-markdown';
import CommitReview from './CommitReview';

const toDateValue = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (ts._seconds !== undefined) {
    return new Date(ts._seconds * 1000 + (ts._nanoseconds || 0) / 1e6);
  }
  return new Date(ts);
};

const MerlinAnalysis = () => {
  const [ap, setAp] = useState(null);
  const [issueId, setIssueId] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isPostingQuestions, setIsPostingQuestions] = useState(false);
  const [questionsPosted, setQuestionsPosted] = useState(false);
  const [analysisExists, setAnalysisExists] = useState(null);
  const [analysisDate, setAnalysisDate] = useState(null);
  const [isAnalysisOutdated, setIsAnalysisOutdated] = useState(false);
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [isUpdatingAnalysis, setIsUpdatingAnalysis] = useState(false);
  const [issueUpdateTime, setIssueUpdateTime] = useState(null);
  const [loadingDots, setLoadingDots] = useState('');
  const [analysisLogId, setAnalysisLogId] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState({});
  const [visibleTabs, setVisibleTabs] = useState(['Overview', 'Solution', 'Workaround', 'Recommendations', 'Open Questions', 'Feedback']);
  const [expandedCodeChanges, setExpandedCodeChanges] = useState({});
  const [draggedTab, setDraggedTab] = useState(null);
  const [dragOverTab, setDragOverTab] = useState(null);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const moreDropdownRef = useRef(null);
  const [hasCommits, setHasCommits] = useState(false);
  const [viewType, setViewType] = useState('analysis'); // 'analysis' or 'review'
  const [isAccepting, setIsAccepting] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [progressQueue, setProgressQueue] = useState([]);
  const [lastDisplayedTimestamp, setLastDisplayedTimestamp] = useState(null);
  const [isDisplayingProgress, setIsDisplayingProgress] = useState(false);
  const [repositoryStatuses, setRepositoryStatuses] = useState([]);
  const [showOutOfDatePopup, setShowOutOfDatePopup] = useState(false);
  const [hasAttachments, setHasAttachments] = useState(false);
  const outOfDatePopupRef = useRef(null);
  const lastDisplayedTimestampRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const currentProgressPercentRef = useRef(0);
  const isUpdatingAnalysisRef = useRef(false);
  const pendingAnalysisRef = useRef(null);
  

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (outOfDatePopupRef.current && !outOfDatePopupRef.current.contains(event.target)) {
        setShowOutOfDatePopup(false);
      }
    };

    if (showOutOfDatePopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOutOfDatePopup]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
        setMoreDropdownOpen(false);
      }
    };

    if (moreDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [moreDropdownOpen]);

  useEffect(() => {
    const initializeAP = async () => {
      if (window.AP) {
        try {
          await new Promise((resolve) => window.AP.context.getContext(resolve));
          setAp(window.AP);
          setIsLoading(false);
        } catch (error) {
          console.error('Error initializing AP:', error);
          setError('Error initializing Atlassian Connect. Please refresh the page.');
          setIsLoading(false);
        }
      } else {
        console.error('AP is not available');
        setError('Atlassian Connect is not available. Please check your installation.');
        setIsLoading(false);
      }
    };

    initializeAP();
  }, []);

  useEffect(() => {
    if (ap) {
      fetchIssueContext();
      checkForUpdates();
      fetchRepositoryStatuses();
    }
  }, [ap]);

  // Polling for repository status updates (out-of-date indicator)
  // This automatically refreshes the UI when repository syncStatus changes
  useEffect(() => {
    if (!ap) return;

    // Poll every 30 seconds
    const interval = setInterval(() => {
      // Don't poll if tab is not visible
      if (!document.hidden) {
        fetchRepositoryStatuses();
      }
    }, 30000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [ap]);

  const fetchRepositoryStatuses = async () => {
    if (!ap) return;
    try {
      const token = await ap.context.getToken();
      const context = await ap.context.getContext();
      const projectId = context.jira.project.id;
      
      const response = await fetch(`/get-github-repos`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        if (result.repositories) {
          // Filter repositories by current project ID
          const projectRepos = result.repositories.filter(repo => 
            repo.projectIds && repo.projectIds.includes(projectId)
          );
          setRepositoryStatuses(projectRepos);
        }
      }
    } catch (error) {
      console.error('Error fetching repository statuses:', error);
    }
  };

  // Polling for repository status updates (out-of-date indicator)
  // This automatically refreshes the UI when repository syncStatus changes
  useEffect(() => {
    if (!ap) return;

    // Poll every 30 seconds
    const interval = setInterval(() => {
      // Don't poll if tab is not visible
      if (!document.hidden) {
        fetchRepositoryStatuses();
      }
    }, 30000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [ap]);


  // Dynamic resize based on content - debounced to prevent shaking
  useEffect(() => {
    if (!ap) return;
    
    let resizeTimeout = null;
    let lastHeight = 0;
    
    const resizeToContent = () => {
      const container = document.getElementById('merlin-analysis-container');
      if (container) {
        const height = container.scrollHeight + 50;
        // Only resize if height changed significantly (more than 20px)
        if (Math.abs(height - lastHeight) > 20) {
          lastHeight = height;
          ap.resize('100%', `${height}px`);
        }
      }
    };

    // Debounced resize
    const debouncedResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeToContent, 100);
    };

    // Initial resize after a short delay
    setTimeout(resizeToContent, 200);

    // Observe content changes
    const observer = new MutationObserver(debouncedResize);

    const container = document.getElementById('merlin-analysis-container');
    if (container) {
      observer.observe(container, { 
        childList: true, 
        subtree: true,
      });
    }

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      observer.disconnect();
    };
  }, [ap, activeTab, expandedCodeChanges]);

  const fetchIssueContext = useCallback(async () => {
    if (!ap || !ap.context) {
      setError('AP context is not available. Please try again later.');
      setIsLoading(false);
      return;
    }

    try {
      // Get issue context
      const context = await ap.context.getContext();
      const issueKey = context.jira.issue.key;
      const projectId = context.jira.project.id;
      
      // Storage key for saving last displayed timestamp
      const storageKey = `merlin_last_displayed_${projectId}_${issueKey}`;

      // Get full issue details including update time
      const issueResponse = await ap.request(`/rest/api/3/issue/${issueKey}`);
      const issueData = JSON.parse(issueResponse.body);
      const newUpdateTime = new Date(issueData.fields.updated);
      console.log('Issue Update Time:', newUpdateTime.toISOString());
      
      // Check if issue has attachments
      const attachments = issueData.fields?.attachment || [];
      setHasAttachments(attachments.length > 0);
      
      // Update state with issue info
      setIssueId(issueKey);
      setSelectedProject({ id: projectId, name: projectId });
      setIssueUpdateTime(newUpdateTime);

      // Fetch analysis
      const token = await ap.context.getToken();
      const analysisResponse = await fetch(`/get-analysis?projectId=${projectId}&issueId=${issueKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!analysisResponse.ok) {
        const errorData = await analysisResponse.json();
        if (analysisResponse.status === 404 && errorData.error === 'PROJECT_NOT_CONFIGURED') {
          setError(errorData.message);
          return;
        }
        throw new Error(`HTTP error! status: ${analysisResponse.status}`);
      }

      const result = await analysisResponse.json();
      console.log('MerlinAnalysis fetchIssueContext result:', {
        status: result.status,
        progressPercent: result.progressPercent,
        progressMessage: result.progressMessage,
        hasAnalysis: !!result.analysis,
        progressHistoryLength: (result.progressHistory || []).length,
        updateInProgress: result.updateInProgress
      });
      
      // Update status
      setAnalysisStatus(result.status);
      
      // Check for in-progress or queued analysis
      if (result.status === 'in_progress' || result.status === 'queued') {
        // If analysis already exists or progress is 100% (race condition - analysis completed but status not updated yet)
        if ((result.analysis && Object.keys(result.analysis).length > 0) || result.progressPercent === 100) {
          setAnalysisExists(true);
          setIsGeneratingAnalysis(false);
          setIsUpdatingAnalysis(false);
          isUpdatingAnalysisRef.current = false;
          setAnalysis(result.analysis || null);
          const parsedDate = toDateValue(result.generatedAt);
          setAnalysisDate(parsedDate ? parsedDate.toISOString() : null);
          setProgressPercent(100);
          setProgressMessage('Complete');
          currentProgressPercentRef.current = 100;
          setProgressQueue([]);
          setIsDisplayingProgress(false);
          setLastDisplayedTimestamp(null);
          lastDisplayedTimestampRef.current = null;
          if (result.analysisLogId) {
            setAnalysisLogId(result.analysisLogId);
          }
          setIsLoading(false);
          return;
        }
        
        const hasExistingAnalysis = (result.analysis && Object.keys(result.analysis).length > 0) || 
                                     (analysis && Object.keys(analysis).length > 0) ||
                                     analysisExists === true;
        // Include updateInProgress from backend to persist state across tab changes/ticket re-entry
        const isUpdate = result.updateInProgress === true || isUpdatingAnalysisRef.current || hasExistingAnalysis;
        
        setIsUpdatingAnalysis(isUpdate);
        isUpdatingAnalysisRef.current = isUpdate;
        
        if (isUpdate) {
          setAnalysisExists(true);
          if (!analysis && result.analysis && Object.keys(result.analysis).length > 0) {
            setAnalysis(result.analysis);
            const parsedDate = toDateValue(result.generatedAt);
            setAnalysisDate(parsedDate ? parsedDate.toISOString() : null);
          }
        } else {
          setAnalysisExists(false);
          setAnalysis(null);
        }
        setIsGeneratingAnalysis(true);

        const history = result.progressHistory || [];
        
        let highestCheckpoint = null;
        let highestPercent = 0;
        
        if (history.length > 0) {
          // Find checkpoint with highest percent
          for (const cp of history) {
            if (cp.percent > highestPercent) {
              highestPercent = cp.percent;
              highestCheckpoint = cp;
            }
          }
        }

        console.log('MerlinAnalysis resume in-progress from backend:', {
          status: result.status,
          backendProgressPercent: result.progressPercent,
          backendProgressMessage: result.progressMessage,
          historyLength: history.length,
          highestCheckpoint,
          highestPercent
        });

        // Set UI to the highest checkpoint (allows jump from 20% to 70%)
        if (highestCheckpoint) {
          setProgressPercent(highestCheckpoint.percent);
          setProgressMessage(highestCheckpoint.message);
          const highestTs = highestCheckpoint.timestamp;
          const highestTsDate = toDateValue(highestTs);
          setLastDisplayedTimestamp(highestTs);
          lastDisplayedTimestampRef.current = highestTs;
          currentProgressPercentRef.current = highestCheckpoint.percent; // Update ref for polling filter
          // Save to localStorage for persistence across page reloads
          if (highestTsDate) {
            localStorage.setItem(storageKey, highestTsDate.getTime().toString());
          }
        } else {
          setProgressPercent(0);
          setProgressMessage('Starting analysis...');
          setLastDisplayedTimestamp(null);
          lastDisplayedTimestampRef.current = null;
          currentProgressPercentRef.current = 0;
          localStorage.removeItem(storageKey);
        }

        // Queue only checkpoints with higher percent than current (don't show skipped ones)
        // This prevents going back to 35%, 40% etc. if we jumped from 20% to 70%
        const futureCheckpoints = history.filter((cp) => {
          return cp.percent > highestPercent;
        });

        // Sort by timestamp to ensure chronological order
        futureCheckpoints.sort((a, b) => {
          const timeA = toDateValue(a.timestamp);
          const timeB = toDateValue(b.timestamp);
          if (!timeA || !timeB) return 0;
          return timeA.getTime() - timeB.getTime();
        });

        if (futureCheckpoints.length > 0) {
          setProgressQueue(futureCheckpoints);
          setIsDisplayingProgress(true);
        } else {
          setProgressQueue([]);
          setIsDisplayingProgress(false);
        }

        if (result.analysisLogId) {
          setAnalysisLogId(result.analysisLogId);
        }
        setIsLoading(false);
        startPolling(projectId, issueKey);
        return;
      }
      
      // Check for analysis_failed status
      if (result.status === 'analysis_failed') {
        setError(result.errorMessage || 'Analysis failed. Please provide a more detailed description.');
        setAnalysisExists(false);
        setIsGeneratingAnalysis(false);
        setIsUpdatingAnalysis(false);
        isUpdatingAnalysisRef.current = false;
        setAnalysis(null);
        setIsLoading(false);
        return;
      }
      
      // Update analysis state
      setAnalysisExists(!!result.analysis);
      const parsedDate = toDateValue(result.generatedAt);
      setAnalysisDate(parsedDate ? parsedDate.toISOString() : null);
      setAnalysis(result.analysis);
      setProgressPercent(100);
      setProgressMessage('Complete');
      currentProgressPercentRef.current = 100;
      setProgressQueue([]);
      setIsDisplayingProgress(false);
      setLastDisplayedTimestamp(null);
      lastDisplayedTimestampRef.current = null;
      if (result.analysisLogId) {
        console.log('Setting analysisLogId from context:', result.analysisLogId);
        setAnalysisLogId(result.analysisLogId);
      }

      // Check if update is still in progress based on backend flag
      // This handles the case when user returns to ticket during an ongoing update
      if (result.updateInProgress === true) {
        setIsUpdatingAnalysis(true);
        isUpdatingAnalysisRef.current = true;
        setIsGeneratingAnalysis(true);
        // Set existing analysis data so UI shows while updating
        setAnalysisExists(!!result.analysis);
        setAnalysis(result.analysis);
        // Only update analysisDate if we have a valid generatedAt, otherwise keep existing date
        if (result.generatedAt) {
          const parsedDate = toDateValue(result.generatedAt);
          if (parsedDate) {
            setAnalysisDate(parsedDate.toISOString());
          }
        }
        // If no generatedAt and no existing analysisDate, don't set it to null (would show 1970)
        setIsLoading(false);
        startPolling(projectId, issueKey);
        return;
      }

      // Compare dates for outdated check
      if (newUpdateTime && result.generatedAt) {
        const analysisDate = toDateValue(result.generatedAt);
        if (analysisDate) {
          const isOutdated = analysisDate < newUpdateTime;          
          setIsAnalysisOutdated(isOutdated);
        }
      }
      
      setIsLoading(false);

    } catch (error) {
      console.error('Error fetching issue context or analysis:', error);
      setError(error.message || 'Failed to load issue data');
      setIsLoading(false);
    }
  }, [ap]);

  useEffect(() => {
    if (!ap || !issueId || !selectedProject?.id) return;
    if (!analysisExists || isGeneratingAnalysis || isAnalysisOutdated) return;
    
    let cancelled = false;
    
    const pollForUpdates = async () => {
      try {
        const token = await ap.context.getToken();
        const response = await fetch(
          `/check-issue-updated?projectId=${selectedProject.id}&issueId=${issueId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `JWT ${token}`,
              'Accept': 'application/json'
            },
            credentials: 'include'
          }
        );
        
        if (!response.ok || cancelled) {
          return;
        }
        
        const result = await response.json();
        
        if (result && result.contentChanged) {
          console.log('[setIsAnalysisOutdated] pollForUpdates - Content changed detected:', {
            location: 'pollForUpdates',
            previousValue: isAnalysisOutdated,
            newValue: true,
            contentChanged: result.contentChanged,
            reason: 'Backend detected content change via polling'
          });
          setIsAnalysisOutdated(true);
        }
      } catch (error) {
        console.error('Error polling for issue updates:', error);
      }
    };
    
    // Poll every 1 second while issue is open
    const interval = setInterval(pollForUpdates, 1000);
    
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [ap, selectedProject, issueId, analysisExists, isGeneratingAnalysis, isAnalysisOutdated]);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    console.log('Submitting feedback with analysisLogId:', analysisLogId);
    if (!analysisLogId) {
      console.error('No analysis log ID available');
      return;
    }
    
    if (!selectedProject || !selectedProject.id) {
      console.error('Project not loaded');
      setError('Project not loaded. Please refresh the page.');
      return;
    }

    try {
      const token = await ap.context.getToken();
      const response = await fetch('/submit-feedback', {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: selectedProject.id,
          issueId: issueId,
          analysisLogId,
          rating,
          comment
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      // Track feedback submission
      trackEvent('submit_feedback', {
        rating: rating,
        has_comment: !!comment,
        analysis_id: analysisLogId
      });

      // Clear form
      setRating(0);
      setComment('');
      
      // Fetch updated feedback list
      await fetchFeedback();
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Use AP from state if available
      ap?.flag?.create?.({
        type: 'error',
        title: 'Error',
        body: 'Failed to submit feedback. Please try again.',
        close: 'auto'
      });
    }
  };

  const handleAcceptSolution = async () => {
    if (!selectedProject || !selectedProject.id) {
      alert('Project not loaded. Please refresh the page.');
      return;
    }
    
    setIsAccepting(true);
    try {
      const result = await invoke('accept-solution', { projectId: selectedProject.id, issueId: issueId });
      alert(result.message);
    } catch (error) {
      console.error('Error accepting solution:', error);
      alert('Failed to accept solution. Please try again later.');
    } finally {
      setIsAccepting(false);
    }
  };

  // Minimal helper used by handleAcceptSolution
  const invoke = async (endpoint, payload) => {
    const token = await ap.context.getToken();
    const res = await fetch(`/${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': `JWT ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload || {})
    });
    if (!res.ok) {
      throw new Error(`Request failed: ${res.status}`);
    }
    return res.json();
  };

  const openDetailedView = () => {
    if (!selectedProject || !selectedProject.id) {
      console.error('Project not loaded');
      return;
    }
    
    ap.dialog.create({
      key: 'detailedMerlinAnalysis',
      chrome: false,
      width: '80%',
      height: '80%',
      url: `/issue-panel?fullView=true&issueId=${issueId}&projectId=${selectedProject.id}`,
    });
  };

  const handleTabSelect = (index) => {
    // Map visible tab index to actual tab name
    const tabName = visibleTabs[index];
    console.log('Tab selected:', tabName);
    setActiveTab(index);
    
    trackEvent('tab_view', { tab_name: tabName });
    
    if (tabName === 'Feedback') {
      console.log('Feedback tab selected, fetching feedback...');
      fetchFeedback();
    }
  };

  const toggleCodeChange = (index) => {
    setExpandedCodeChanges(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Drag and drop handlers for tab reordering
  const handleDragStart = (e, tabName) => {
    setDraggedTab(tabName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, tabName) => {
    e.preventDefault();
    if (tabName !== draggedTab) {
      setDragOverTab(tabName);
    }
  };

  const handleDragLeave = () => {
    setDragOverTab(null);
  };

  const handleDrop = (e, targetTabName) => {
    e.preventDefault();
    if (!draggedTab || draggedTab === targetTabName) {
      setDraggedTab(null);
      setDragOverTab(null);
      return;
    }

    const newTabs = [...visibleTabs];
    const draggedIndex = newTabs.indexOf(draggedTab);
    const targetIndex = newTabs.indexOf(targetTabName);
    
    // Remove dragged tab and insert at new position
    newTabs.splice(draggedIndex, 1);
    newTabs.splice(targetIndex, 0, draggedTab);
    
    setVisibleTabs(newTabs);
    
    // Update activeTab if needed
    const currentActiveTabName = visibleTabs[activeTab];
    const newActiveIndex = newTabs.indexOf(currentActiveTabName);
    setActiveTab(newActiveIndex);
    
    setDraggedTab(null);
    setDragOverTab(null);
  };

  const handleDragEnd = () => {
    setDraggedTab(null);
    setDragOverTab(null);
  };

  const handlePostQuestions = async () => {
    trackEvent('post_questions');
    if (!analysis?.additionalQuestions || !ap || questionsPosted) {
      return;
    }

    setIsPostingQuestions(true);
    try {
      const token = await ap.context.getToken();
      const context = await ap.context.getContext();
      const issueKey = context.jira.issue.key;

      const questionsText = `*Clarifying Questions from CodeMerlin:*\n${analysis.additionalQuestions
        .map(q => `• ${q}`)
        .join('\n')}`;

      const response = await fetch(`/post-comment?issueId=${issueKey}`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comment: questionsText })
      });

      if (!response.ok) {
        throw new Error('Failed to post comment');
      }

      ap.flag.success({ body: 'Questions posted as a comment!' });
      setQuestionsPosted(true);
      
      // Refresh the Jira issue page to show the new comment
      if (ap.jira && ap.jira.refreshIssuePage) {
        console.log('Refreshing Jira issue page after posting questions...');
        ap.jira.refreshIssuePage();
      } else {
        console.warn('AP.jira.refreshIssuePage not available');
      }
    } catch (error) {
      console.error('Error posting questions:', error);
      ap.flag.error({ body: 'Failed to post questions. Please try again.' });
    } finally {
      setIsPostingQuestions(false);
    }
  };

  const handleGenerateAnalysis = async () => {
    
    if (!selectedProject || !selectedProject.id) {
      setError('Project not loaded. Please refresh the page.');
      return;
    }
    
    if (isGeneratingAnalysis || analysisStatus === 'in_progress' || analysisStatus === 'queued') {
      return;
    }
    
    setIsGeneratingAnalysis(true);
    setError(null);
    setProgressPercent(0);
    setProgressMessage('Starting analysis...');
    currentProgressPercentRef.current = 0;
    
    try {
      const token = await ap.context.getToken();
      const response = await fetch('/generate-analysis', {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: selectedProject.id,
          issueId: issueId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate analysis');
      }

      const result = await response.json();
      
      // Update status
      setAnalysisStatus(result.status);
      setProgressPercent(0);
      setProgressMessage('Starting analysis...');
      currentProgressPercentRef.current = 0;
      if (result.analysisLogId) {
        setAnalysisLogId(result.analysisLogId);
      }
      
      // Handle different states
      switch (result.status) {
        case 'waiting_for_indexing':
          setError(`Unable to generate analysis at this time.\n\nRepositories: ${result.unindexedRepos}\n\nThis may be because indexing is still in progress or new commits have been detected. Please try again in a few minutes.`);
          setIsGeneratingAnalysis(false);
          return;
          
        case 'analysis_in_progress':
        case 'queued':
          // Start polling with normal flow
          startPolling(selectedProject.id, issueId);
          break;
        
        case 'no_repositories_found':
          setError(result.message || 'No code repositories found for analysis. Please map your repositories for this project in the CodeMerlin configuration to generate an analysis.');
          setIsGeneratingAnalysis(false);
          return;
          
        case 'error':
          setError(result.message || 'Failed to generate analysis');
          setIsGeneratingAnalysis(false);
          return;
          
        case 'analysis_failed':
          setError(result.errorMessage || 'Analysis failed. Please provide a more detailed description of the issue.');
          setIsGeneratingAnalysis(false);
          return;
          
        default:
          // Normal flow for 'started' status
          startPolling(selectedProject.id, issueId);
      }

    } catch (error) {
      console.error('Error generating analysis:', error);
      setError('Failed to generate analysis. Please try again.');
      setIsGeneratingAnalysis(false);
    }
  };

  // Separate polling logic for cleaner code
  const startPolling = useCallback((projectId, currentIssueId) => {
    // Clear existing interval if any
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setLoadingDots(''); // Reset dots at start

    console.log('Starting polling with:', { projectId, currentIssueId });

    const pollInterval = setInterval(async () => {
      try {
        
        if (!projectId || !currentIssueId) {
          console.warn('Project or Issue ID not available for polling', { projectId, currentIssueId });
          return;
        }
        
        const token = await ap.context.getToken();
        const response = await fetch(`/get-analysis?projectId=${projectId}&issueId=${currentIssueId}`, {
          method: 'GET',
          headers: {
            'Authorization': `JWT ${token}`,
            'Accept': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        console.log('MerlinAnalysis polling tick:', {
          status: result.status,
          backendProgressPercent: result.progressPercent,
          backendProgressMessage: result.progressMessage,
          hasAnalysis: !!result.analysis,
          progressHistoryLength: (result.progressHistory || []).length,
          progressHistoryPreview: result.progressHistory ? result.progressHistory.slice(-3) : [],
          // localProgressPercent is from closure, might be stale but only for logging
          lastDisplayedTimestamp: lastDisplayedTimestampRef.current
        });
        
        // Update status
        setAnalysisStatus(result.status);
        if (result.analysisLogId) {
          setAnalysisLogId(result.analysisLogId);
        }

        const newProgressHistory = result.progressHistory || [];
        // Use ref for latest displayed time
        const lastTimeValue = lastDisplayedTimestampRef.current;
        const lastTime = lastTimeValue ? toDateValue(lastTimeValue) : null;
        
        // Get current displayed percent from ref to prevent going backwards
        const currentPercent = currentProgressPercentRef.current;

        const newCheckpoints = newProgressHistory.filter((checkpoint) => {
          // Filter by timestamp (newer than last displayed)
          if (lastTime) {
            const checkpointTime = toDateValue(checkpoint.timestamp);
            if (!checkpointTime) return false;
            if (checkpointTime <= lastTime) return false;
          }
          // Also filter by percent - don't add checkpoints with lower or equal percent than current
          // This prevents going back to skipped percentages (e.g., 35%, 40% when at 70%)
          return checkpoint.percent > currentPercent;
        });

        if (newCheckpoints.length > 0) {
          // Sort checkpoints by timestamp to ensure correct order
          const sortedNewCheckpoints = newCheckpoints.sort((a, b) => {
            const timeA = toDateValue(a.timestamp);
            const timeB = toDateValue(b.timestamp);
            if (!timeA || !timeB) return 0;
            return timeA.getTime() - timeB.getTime();
          });

          setProgressQueue((currentQueue) => {
            const existingKeys = new Set(
              currentQueue.map((cp) => {
                const ts = toDateValue(cp.timestamp);
                return `${cp.percent}-${ts ? ts.getTime() : ''}`;
              })
            );

            const filtered = sortedNewCheckpoints.filter((cp) => {
              const ts = toDateValue(cp.timestamp);
              const key = `${cp.percent}-${ts ? ts.getTime() : ''}`;
              if (existingKeys.has(key)) {
                return false;
              }
              existingKeys.add(key);
              return true;
            });

            if (filtered.length === 0) return currentQueue;
            
            // Merge and sort the entire queue to maintain chronological order
            const merged = [...currentQueue, ...filtered];
            return merged.sort((a, b) => {
              const timeA = toDateValue(a.timestamp);
              const timeB = toDateValue(b.timestamp);
              if (!timeA || !timeB) return 0;
              return timeA.getTime() - timeB.getTime();
            });
          });
          setIsDisplayingProgress(true);
        }
        
        // Check for analysis_failed
        if (result.status === 'analysis_failed') {
          clearInterval(pollInterval);
          pollingIntervalRef.current = null;
          setIsGeneratingAnalysis(false);
          setIsUpdatingAnalysis(false);
          isUpdatingAnalysisRef.current = false;
          setAnalysisExists(false);
          setLoadingDots('');
          setIsDisplayingProgress(false);
          setError(result.errorMessage || 'Analysis failed. Please provide a more detailed description.');
          
          // Refresh Jira page even on failure to restore original title
          if (ap && ap.jira && ap.jira.refreshIssuePage) {
            console.log('Refreshing Jira issue page after analysis failed...');
            setTimeout(() => {
              ap.jira.refreshIssuePage();
            }, 2000);
          }
          
          return;
        }
        
        // Check if still in progress
        if (result.status === 'in_progress' || result.status === 'queued') {
          // Continue polling, progress will be shown via queue
          return;
        }
        
        // Analysis completed - stop polling, but DON'T show analysis yet
        // Analysis will be shown after progress checkpoints reach 100%
        if (result.status === 'completed' && result.analysis) {
          clearInterval(pollInterval);
          pollingIntervalRef.current = null;

          // Store analysis in ref, will be displayed when progress reaches 100%
          pendingAnalysisRef.current = {
            analysis: result.analysis,
            generatedAt: result.generatedAt
          };
          setAnalysisStatus('completed');
          
          // Refresh Jira page to show updated issue title when analysis completes
          // This happens even if progress hasn't reached 100% yet
          if (ap && ap.jira && ap.jira.refreshIssuePage) {
            console.log('Refreshing Jira issue page after analysis completed...');
            // Delay refresh slightly to ensure backend has updated the title
            setTimeout(() => {
              ap.jira.refreshIssuePage();
            }, 2000);
          }
          
          // Continue displaying progress checkpoints until 100%
          return;
        }
        
          // Just add another dot, no reset
          setLoadingDots(dots => dots + '.');
      } catch (error) {
        console.error('Polling error:', error);
        setError(`Error checking analysis status: ${error.message}`);
        clearInterval(pollInterval);
        pollingIntervalRef.current = null;
        setIsGeneratingAnalysis(false);
        setIsUpdatingAnalysis(false);
        isUpdatingAnalysisRef.current = false;
        setIsDisplayingProgress(false);
      }
    }, 10000);

    pollingIntervalRef.current = pollInterval;

    // Clear interval after 8.3 minutes
    setTimeout(() => {
      if (pollingIntervalRef.current === pollInterval) {
      clearInterval(pollInterval);
        pollingIntervalRef.current = null;
        // If interval was still running, force stop and show error
        setIsGeneratingAnalysis(false);
        setIsUpdatingAnalysis(false);
        isUpdatingAnalysisRef.current = false;
        setError('Analysis generation timed out. Please try again.');
        setLoadingDots(''); // Reset dots on timeout
      }
    }, 500000);
  }, [ap]);

  const handleRegenerateAnalysis = async () => {
    if (!selectedProject || !selectedProject.id) {
      setError('Project not loaded. Please refresh the page.');
      return;
    }
    
    if (isGeneratingAnalysis || analysisStatus === 'in_progress' || analysisStatus === 'queued') {
      return;
    }
    
    setIsGeneratingAnalysis(true);
    setIsUpdatingAnalysis(true);
    isUpdatingAnalysisRef.current = true;
    setError(null);
    setProgressPercent(0);
    setProgressMessage('Starting update...');
    currentProgressPercentRef.current = 0;
    // Clear old analysis to show only progress UI during update
    setAnalysis(null);
    pendingAnalysisRef.current = null; // Clear any pending analysis
    
    try {
      const token = await ap.context.getToken();
      const response = await fetch('/generate-analysis', {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: selectedProject.id,
          issueId: issueId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate analysis');
      }

      const result = await response.json();
      
      // Update status
      setAnalysisStatus(result.status);
      setProgressPercent(0);
      setProgressMessage('Starting update...');
      currentProgressPercentRef.current = 0;
      if (result.analysisLogId) {
        setAnalysisLogId(result.analysisLogId);
      }
      
      // Handle different states
      switch (result.status) {
        case 'waiting_for_indexing':
          setError(`Can not generate analysis while repositories are being indexed: ${result.unindexedRepos}.  Please try again in twenty minutes.`);
          setIsGeneratingAnalysis(false);
          setIsUpdatingAnalysis(false);
          isUpdatingAnalysisRef.current = false;
          return;
          
        case 'analysis_in_progress':
        case 'queued':
          // Start polling with normal flow
          startPolling(selectedProject.id, issueId);
          // Refresh context to show UI like when entering ticket
          fetchIssueContext();
          break;
        
        case 'no_repositories_found':
          setError(result.message || 'No code repositories found for analysis. Please map your repositories for this project in the CodeMerlin configuration to generate an analysis.');
          setIsGeneratingAnalysis(false);
          setIsUpdatingAnalysis(false);
          isUpdatingAnalysisRef.current = false;
          return;
          
        case 'error':
          setError(result.message || 'Failed to generate analysis');
          setIsGeneratingAnalysis(false);
          setIsUpdatingAnalysis(false);
          isUpdatingAnalysisRef.current = false;
          return;
          
        case 'analysis_failed':
          setError(result.errorMessage || 'Analysis failed. Please provide a more detailed description of the issue.');
          setIsGeneratingAnalysis(false);
          setIsUpdatingAnalysis(false);
          isUpdatingAnalysisRef.current = false;
          return;
          
        default:
          // Normal flow for 'started' status
          startPolling(selectedProject.id, issueId);
          // Refresh context to show UI like when entering ticket
          fetchIssueContext();
      }

    } catch (error) {
      console.error('Error generating analysis:', error);
      setError('Failed to generate analysis. Please try again.');
      setIsGeneratingAnalysis(false);
      setIsUpdatingAnalysis(false);
      isUpdatingAnalysisRef.current = false;
    }
  };

  const jiraFont = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";
  
  const styles = {
    container: {
      padding: '16px',
      backgroundColor: '#ffffff',
      fontFamily: jiraFont,
      fontSize: '14px',
      lineHeight: '1.42857143',
      color: '#172B4D',
      overflow: 'hidden',
    },
    headerContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '1px solid #dfe1e6',
    },
    leftHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    logo: {
      height: '24px',
      width: 'auto',
    },
    heading: {
      color: '#0052CC',
      fontSize: '18px',
      margin: 0,
      fontFamily: jiraFont,
      fontWeight: 600,
    },
    tabContent: {
      padding: '20px 16px',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box',
    },
    sectionTitle: {
      color: '#172B4D',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: jiraFont,
      marginTop: '20px',
      marginBottom: '12px',
      paddingBottom: '6px',
      borderBottom: '1px solid #dfe1e6',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    paragraph: {
      lineHeight: '1.42857143',
      marginBottom: '14px',
      color: '#172B4D',
      fontSize: '14px',
      fontFamily: jiraFont,
    },
    list: {
      paddingLeft: '20px',
      marginBottom: '14px',
    },
    listItem: {
      marginBottom: '8px',
      lineHeight: '1.42857143',
      color: '#172B4D',
      fontSize: '14px',
      fontFamily: jiraFont,
    },
    codeBlock: {
      marginBottom: '24px',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
    },
    codeTitle: {
      backgroundColor: '#f4f5f7',
      padding: '10px 12px',
      borderBottom: '1px solid #dfe1e6',
      fontWeight: 600,
      fontSize: '14px',
      fontFamily: jiraFont,
      color: '#172B4D',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    code: {
      backgroundColor: '#f4f5f7',
      padding: '12px',
      overflowX: 'auto',
      fontFamily: "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
      fontSize: '12px',
      lineHeight: '1.5',
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#0052CC',
      color: 'white',
      border: 'none',
      borderRadius: '3px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: jiraFont,
      marginTop: '10px',
      transition: 'all 0.15s ease',
      boxShadow: 'none',
    },
    diffContainer: {
      margin: '12px 0',
      border: '1px solid #e9ecef',
      borderRadius: '6px',
      overflow: 'hidden',
    },
    diffWrapper: {
      fontSize: '12px',
      fontFamily: "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
      backgroundColor: '#ffffff',
      border: '1px solid #dfe1e6',
      borderRadius: '4px',
      overflow: 'auto',
    },
    rightHeader: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '12px',
      fontFamily: jiraFont,
      color: '#5e6c84',
    },
    updateButton: {
      backgroundColor: '#0052CC',
      color: 'white',
      border: '1px solid #0052CC',
      borderRadius: '6px',
      padding: '10px 16px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: 500,
      fontFamily: jiraFont,
      transition: 'all 0.15s ease',
      boxShadow: '0 2px 4px rgba(0, 82, 204, 0.25)',
    },
    attachmentDisclaimer: {
      background: '#FFF7E6',
      color: '#172B4D',
      padding: '12px 16px',
      borderRadius: '3px',
      marginBottom: '16px',
      border: '1px solid #FFE380',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      fontFamily: jiraFont,
    },
    attachmentDisclaimerIcon: {
      flexShrink: 0,
      marginTop: '2px',
    },
    attachmentDisclaimerText: {
      fontSize: '14px',
      lineHeight: '1.5',
      color: '#172B4D',
    },
    analysisInProgressBanner: {
      background: '#deebff',
      color: '#0052CC',
      padding: '10px 16px',
      borderRadius: '3px',
      marginBottom: '16px',
      border: '1px solid #b3d4ff',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '14px',
      fontFamily: jiraFont,
    },
    updateProgressBanner: {
      background: '#deebff',
      color: '#0052CC',
      padding: '16px',
      borderRadius: '3px',
      marginBottom: '16px',
      textAlign: 'center',
      border: '1px solid #b3d4ff',
      fontFamily: jiraFont,
    },
    updateProgressContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      fontFamily: jiraFont,
    },
    updateProgressMessage: {
      margin: '8px 0 0 0',
      fontSize: '14px',
      color: '#0052CC',
      fontWeight: 500,
      fontFamily: jiraFont,
    },
    spinner: {
      display: 'inline-block',
      marginLeft: '8px',
      animation: 'spin 2s linear infinite',
    },
    feedbackText: {
      lineHeight: '1.42857143',
      color: '#172B4D',
      marginBottom: '20px',
      fontSize: '14px',
      fontFamily: jiraFont,
      backgroundColor: '#f4f5f7',
      padding: '12px 16px',
      borderRadius: '3px',
      border: '1px solid #dfe1e6',
      borderLeft: '3px solid #0052CC',
    },
    feedbackHistory: {
      marginTop: '24px',
      padding: '16px',
      backgroundColor: '#fafbfc',
      borderRadius: '3px',
      border: '1px solid #dfe1e6',
    },
    subSectionTitle: {
      color: '#172B4D',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: jiraFont,
      marginBottom: '12px',
      paddingBottom: '8px',
      borderBottom: '1px solid #dfe1e6',
    },
    feedbackEntry: {
      backgroundColor: '#ffffff',
      padding: '16px',
      borderRadius: '3px',
      marginBottom: '10px',
      border: '1px solid #dfe1e6',
      boxShadow: 'none',
    },
    feedbackHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '6px',
    },
    stars: {
      display: 'flex',
      alignItems: 'center',
      gap: '3px',
    },
    feedbackDate: {
      color: '#5e6c84',
      fontSize: '12px',
      fontFamily: jiraFont,
    },
    feedbackComment: {
      margin: '10px 0 0 0',
      color: '#172B4D',
      fontSize: '14px',
      fontFamily: jiraFont,
      lineHeight: '1.42857143',
    },
    disclaimer: {
      fontSize: '12px',
      fontFamily: jiraFont,
      color: '#5e6c84',
      padding: '8px 12px',
      borderBottom: '1px solid #dfe1e6',
      backgroundColor: '#fafbfc',
      marginBottom: '12px',
      borderRadius: '3px',
    },
    generateButton: {
      backgroundColor: '#0052CC',
      color: 'white',
      padding: '14px 32px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: jiraFont,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      boxShadow: '0 4px 16px rgba(0, 82, 204, 0.3)',
    },
    progressContainer: {
      width: '200px',
      height: '6px',
      backgroundColor: 'rgba(0, 82, 204, 0.15)',
      borderRadius: '3px',
      marginLeft: '12px',
      display: 'inline-block',
      overflow: 'hidden',
    },
    progressBar: {
      height: '100%',
      background: 'linear-gradient(90deg, #0052CC, #0065FF)',
      borderRadius: '3px',
      transition: 'width 0.4s ease-out',
    },
    viewToggle: {
      display: 'flex',
      gap: '8px',
      marginBottom: '20px',
      padding: '8px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e9ecef',
    },
    activeToggle: {
      padding: '10px 20px',
      backgroundColor: '#0052CC',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: 500,
      boxShadow: '0 2px 4px rgba(0, 82, 204, 0.2)',
    },
    inactiveToggle: {
      padding: '10px 20px',
      backgroundColor: 'transparent',
      color: '#42526E',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'background-color 0.2s ease',
    },
    commitBadge: {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      padding: '3px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
    },
    centerContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      textAlign: 'center'
    },
    outOfDateContainer: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: '12px',
      position: 'relative'
    },
    outOfDateIcon: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: '#FF991F',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
    },
    outOfDatePopup: {
      position: 'absolute',
      top: '32px',
      left: '0',
      backgroundColor: 'white',
      borderRadius: '4px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      minWidth: '280px',
      maxWidth: '400px',
      padding: '12px'
    },
    outOfDatePopupTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#172B4D',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    outOfDateRepoList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    outOfDateRepoItem: {
      padding: '8px 10px',
      backgroundColor: '#FFF7E6',
      borderRadius: '3px',
      marginBottom: '6px',
      fontSize: '13px',
      color: '#172B4D',
      border: '1px solid #FFE380',
      textAlign: 'center',
    }
  };

  const CodeDiff = ({ oldCode, newCode }) => {
    try {
      // Split both code blocks into lines and clean them
      const oldLines = oldCode.split('\n');
      const newLines = newCode.split('\n');
      
      // Find the actual differences
      let diffLines = [];
      let maxLength = Math.max(oldLines.length, newLines.length);
      
      for (let i = 0; i < maxLength; i++) {
        const oldLine = oldLines[i] || '';
        const newLine = newLines[i] || '';
        
        if (oldLine !== newLine) {
          if (oldLine) diffLines.push(`-${oldLine}`);
          if (newLine) diffLines.push(`+${newLine}`);
        } else {
          diffLines.push(` ${oldLine}`); // Unchanged lines
        }
      }

      // Create the diff text with only the changes
      const diffText = `diff --git a/file.js b/file.js
--- a/file.js
+++ b/file.js
@@ -1,${oldLines.length} +1,${newLines.length} @@
${diffLines.join('\n')}`;

      const files = parseDiff(diffText);

      return (
        <div style={{
          fontSize: '13px',
          fontFamily: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
          border: '1px solid #d0d7de',
          borderRadius: '6px',
          overflow: 'auto',
          backgroundColor: '#ffffff'
        }}>
          {files.map((file, i) => (
            <Diff
              key={i}
              viewType="unified"
              diffType={file.type}
              hunks={file.hunks}
              gutterType="default"
              className="diff-view"
            >
              {hunks => hunks.map(hunk => (
                <Hunk
                  key={hunk.content}
                  hunk={hunk}
                />
              ))}
            </Diff>
          ))}
        </div>
      );
    } catch (error) {
      console.error('Error in CodeDiff:', error);
      return <div>Error displaying diff</div>;
    }
  };

  const trackEvent = async (eventName, params = {}) => {
    try {
      const token = await ap.context.getToken();
      await fetch('/analytics', {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event: eventName,
          params: params
        })
      });
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  };

  // Check if any repository is out-of-date and get their names
  const outOfDateRepositories = repositoryStatuses.filter(repo => repo.syncStatus === 'out-of-date');
  const hasOutOfDateRepositories = outOfDateRepositories.length > 0;

  const renderHeader = () => (
    <div style={styles.headerContainer}>
      <div style={styles.leftHeader}>
        <img src="/images/codemerlinlogo.png" alt="CodeMerlin Logo" style={styles.logo} />
        <h1 style={styles.heading}>CodeMerlin Analysis</h1>
        {hasOutOfDateRepositories && (
          <div style={styles.outOfDateContainer} ref={outOfDatePopupRef}>
            <div 
              style={styles.outOfDateIcon}
              onClick={() => setShowOutOfDatePopup(!showOutOfDatePopup)}
              title="Some repositories are out-of-date"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            {showOutOfDatePopup && (
              <div style={styles.outOfDatePopup}>
                <div style={styles.outOfDatePopupTitle}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#FF991F">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  Out-of-date Repositories
                </div>
                <ul style={styles.outOfDateRepoList}>
                  {outOfDateRepositories.map(repo => (
                    <li key={repo.id} style={styles.outOfDateRepoItem}>
                      {repo.owner}/{repo.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      {analysisExists && (
        <div style={styles.rightHeader}>
          {analysisDate && (
            <span>Analysis Generated: {new Date(analysisDate).toLocaleString()}</span>
          )}
          {(isAnalysisOutdated || hasOutOfDateRepositories) && (
            <button 
              onClick={handleRegenerateAnalysis}
              style={styles.updateButton}
              disabled={isGeneratingAnalysis}
              onMouseEnter={(e) => {
                if (!isGeneratingAnalysis) {
                  e.target.style.backgroundColor = '#0065FF';
                  e.target.style.borderColor = '#0065FF';
                }
              }}
              onMouseLeave={(e) => {
                if (!isGeneratingAnalysis) {
                  e.target.style.backgroundColor = '#0052CC';
                  e.target.style.borderColor = '#0052CC';
                }
              }}
            >
              {isGeneratingAnalysis ? 'Updating...' : 'Update Analysis'}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    if (error) {
      return (
        <div style={styles.centerContent}>
          <div style={styles.error}>{error}</div>
          <button 
            onClick={handleGenerateAnalysis} 
            style={{ ...styles.generateButton, marginTop: '24px' }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#0065FF';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(0, 82, 204, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#0052CC';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 16px rgba(0, 82, 204, 0.3)';
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div style={styles.centerContent}>
          <p>Loading...</p>
        </div>
      );
    }

    // Check if analysis is in progress (from status or state)
    const isAnalysisInProgress = isGeneratingAnalysis || analysisStatus === 'in_progress' || analysisStatus === 'queued';
    
    if (analysisExists === false && !isAnalysisInProgress) {
      return (
        <div style={styles.centerContent}>
          <button 
            onClick={handleGenerateAnalysis}
            style={styles.generateButton}
            disabled={isGeneratingAnalysis}
            onMouseEnter={(e) => {
              if (!isGeneratingAnalysis) {
                e.target.style.backgroundColor = '#0065FF';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 82, 204, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isGeneratingAnalysis) {
                e.target.style.backgroundColor = '#0052CC';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 82, 204, 0.3)';
              }
            }}
          >
            {isGeneratingAnalysis ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Analyzing</span>
                <div style={styles.progressContainer}>
                  <div 
                    style={{
                      ...styles.progressBar,
                      width: `${progressPercent}%`
                    }}
                  />
                </div>
              </div>
            ) : (
              'Run Analysis'
            )}
          </button>
        </div>
      );
    }

    if (isAnalysisInProgress && !analysisExists) {
      return (
        <div style={styles.centerContent}>
          <p style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 500, color: '#172B4D' }}>
            Analysis in progress{loadingDots}
          </p>
          <div style={styles.progressContainer}>
            <div 
              style={{
                ...styles.progressBar,
                width: `${progressPercent}%`
              }}
            />
          </div>
        </div>
      );
    }

    if (analysisExists === null || analysisExists === undefined) {
      return (
        <div style={styles.centerContent}>
          <p>Loading analysis...</p>
        </div>
      );
    }

    // If analysis exists flag is true but analysis object is null, show updating UI
    if (analysisExists && !analysis && isAnalysisInProgress) {
      return (
        <div style={styles.centerContent}>
          <p style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 500, color: '#172B4D' }}>
            {isUpdatingAnalysis ? 'Updating analysis' : 'Analysis in progress'}{loadingDots}
          </p>
          <div style={styles.progressContainer}>
            <div 
              style={{
                ...styles.progressBar,
                width: `${progressPercent}%`
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Show update progress banner with progress bar when updating existing analysis */}
        {isUpdatingAnalysis && isAnalysisInProgress && (
          <div style={styles.updateProgressBanner}>
            <div style={styles.updateProgressContent}>
              <span>Updating analysis{loadingDots}</span>
              <div style={styles.progressContainer}>
                <div 
                  style={{
                    ...styles.progressBar,
                    width: `${progressPercent}%`
                  }}
                />
              </div>
            </div>
            {progressMessage && (
              <p style={styles.updateProgressMessage}>
                {progressMessage} ({progressPercent}%)
              </p>
            )}
          </div>
        )}
        {/* Show regular banner for non-update generation */}
        {isGeneratingAnalysis && !isUpdatingAnalysis && (
          <div style={styles.analysisInProgressBanner}>
            Generating updated analysis...
          </div>
        )}
        <Tabs selectedIndex={activeTab} onSelect={handleTabSelect}>
          <TabList>
            {/* Show first 3 tabs directly */}
            {visibleTabs.map((tabName, index) => {
              return (
                <Tab 
                  key={tabName} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, tabName)}
                  onDragOver={(e) => handleDragOver(e, tabName)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, tabName)}
                  onDragEnd={handleDragEnd}
                  style={{ 
                    opacity: draggedTab === tabName ? 0.5 : 1,
                    outline: dragOverTab === tabName ? '2px solid #0052CC' : undefined,
                    cursor: 'grab',
                  }}
                >
                  {tabName}
                </Tab>
              );
            })}
          </TabList>

          {/* Render TabPanels in the same order as visibleTabs */}
          {visibleTabs.map((tabName) => {
            if (tabName === 'Overview') {
              return (
                <TabPanel key={tabName} style={styles.tabContent}>
                  <h2 style={styles.sectionTitle}>Original Issue Information</h2>
                  <p><strong>Repository:</strong> <a href={analysis.repository} target="_blank" rel="noopener noreferrer">{analysis.repositories}</a></p>
                  <p><strong>Issue:</strong> <a href={analysis.issueLink} target="_blank" rel="noopener noreferrer">{analysis.issueTitle} (#{analysis.issueNumber})</a></p>
                  <h2 style={styles.sectionTitle}>Root Cause</h2>
                  <p style={styles.paragraph}>{analysis.rootCause}</p>
                </TabPanel>
              );
            }
            
            if (tabName === 'Solution') {
              return (
                <TabPanel key={tabName} style={styles.tabContent}>
                  <h2 style={styles.sectionTitle}>Solution</h2>
                  <p style={styles.paragraph}>{analysis.solution}</p>
                  
                  <h3 style={styles.sectionTitle}>Explanation of Changes</h3>
                  <ul style={styles.list}>
                    {analysis.explanation?.map((item, idx) => (
                      <li key={idx} style={styles.listItem}>{item}</li>
                    ))}
                  </ul>
                  
                  <h2 style={styles.sectionTitle}>Code Changes ({analysis.codeChanges?.length || 0} files)</h2>
                  {analysis.codeChanges?.map((change, idx) => (
                    <div key={idx} style={{
                      marginBottom: '12px',
                      border: '1px solid #dfe1e6',
                      borderRadius: '4px',
                    }}>
                      <div
                        onClick={() => toggleCodeChange(idx)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          backgroundColor: expandedCodeChanges[idx] ? '#deebff' : '#f4f5f7',
                          cursor: 'pointer',
                          userSelect: 'none',
                          borderBottom: expandedCodeChanges[idx] ? '1px solid #dfe1e6' : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            fontSize: '10px',
                            color: '#5e6c84',
                            transform: expandedCodeChanges[idx] ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}>
                            ▶
                          </span>
                          <span style={{ fontWeight: 500, color: '#0052CC', fontSize: '14px' }}>{change.file}</span>
                          <span style={{ color: '#5e6c84', fontSize: '12px' }}>({change.repository})</span>
                        </div>
                      </div>
                      
                      {expandedCodeChanges[idx] && (
                        <div>
                          <p style={{ padding: '10px 12px', margin: 0, backgroundColor: '#fafbfc', borderBottom: '1px solid #dfe1e6', fontSize: '13px', color: '#42526E' }}>
                            <strong>Description:</strong> {change.description}
                          </p>
                          <div 
                            style={{ maxHeight: '350px', overflowY: 'auto', cursor: 'grab' }} 
                            className="code-diff-container"
                            onMouseDown={(e) => {
                              const container = e.currentTarget;
                              container.style.cursor = 'grabbing';
                              container.dataset.isDragging = 'true';
                              container.dataset.startX = e.pageX;
                              container.dataset.scrollLeft = container.scrollLeft;
                            }}
                            onMouseMove={(e) => {
                              const container = e.currentTarget;
                              if (container.dataset.isDragging !== 'true') return;
                              e.preventDefault();
                              const x = e.pageX;
                              const walk = (x - parseFloat(container.dataset.startX)) * 1.5;
                              container.scrollLeft = parseFloat(container.dataset.scrollLeft) - walk;
                            }}
                            onMouseUp={(e) => {
                              const container = e.currentTarget;
                              container.style.cursor = 'grab';
                              container.dataset.isDragging = 'false';
                            }}
                            onMouseLeave={(e) => {
                              const container = e.currentTarget;
                              container.style.cursor = 'grab';
                              container.dataset.isDragging = 'false';
                            }}
                          >
                            <CodeDiff
                              oldCode={change.currentCode || ''}
                              newCode={change.suggestedCode || ''}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </TabPanel>
              );
            }
            
            if (tabName === 'Workaround') {
              return (
                <TabPanel key={tabName} style={styles.tabContent}>
                  {analysis.potentialWorkaround ? (
                    <>
                      <h2 style={styles.sectionTitle}>Potential Workaround</h2>
                      <p style={styles.paragraph}><strong>Description:</strong> {analysis.potentialWorkaround.description}</p>
                      <h3 style={styles.sectionTitle}>Steps:</h3>
                      <ol style={styles.list}>
                        {analysis.potentialWorkaround.steps.map((step, idx) => (
                          <li key={idx} style={styles.listItem}>{step}</li>
                        ))}
                      </ol>
                      <h3 style={styles.sectionTitle}>Limitations:</h3>
                      <p style={styles.paragraph}>{analysis.potentialWorkaround.limitations}</p>
                    </>
                  ) : (
                    <p>No workaround available for this issue.</p>
                  )}
                </TabPanel>
              );
            }
            
            if (tabName === 'Recommendations') {
              return (
                <TabPanel key={tabName} style={styles.tabContent}>
                  <h2 style={styles.sectionTitle}>Additional Recommendations</h2>
                  <ul style={styles.list}>
                    {analysis.recommendations?.map((item, idx) => (
                      <li key={idx} style={styles.listItem}>{item}</li>
                    ))}
                  </ul>
                  <h2 style={styles.sectionTitle}>Next Steps</h2>
                  <ol style={styles.list}>
                    {analysis.nextSteps?.map((item, idx) => (
                      <li key={idx} style={styles.listItem}>{item}</li>
                    ))}
                  </ol>
                </TabPanel>
              );
            }
            
            if (tabName === 'Open Questions') {
              return (
                <TabPanel key={tabName} style={styles.tabContent}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={styles.sectionTitle}>Open Questions</h2>
                    {!questionsPosted ? (
                      <button
                        onClick={handlePostQuestions}
                        disabled={isPostingQuestions}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#0052CC',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {isPostingQuestions ? 'Posting...' : 'Post Questions to Comments'}
                      </button>
                    ) : (
                      <span style={{ color: '#666', fontSize: '14px' }}>
                        Questions posted to comments
                      </span>
                    )}
                  </div>
                  {analysis.additionalQuestions?.length > 0 && (
                    <ul style={styles.list}>
                      {analysis.additionalQuestions?.map((question, idx) => (
                        <li key={idx} style={styles.listItem}>{question}</li>
                      ))}
                    </ul>
                  )}
                </TabPanel>
              );
            }
            
            if (tabName === 'Feedback') {
              return (
                <TabPanel key={tabName} style={styles.tabContent}>
              <h2 style={styles.sectionTitle}>Analysis Feedback</h2>
              <p style={styles.feedbackText}>
                Your feedback directly trains CodeMerlin! Each rating and comment is fed back to the AI, 
                helping it learn what makes a good analysis and how to improve future responses. 
                Tell us what worked and what didn't - your input actively shapes the system.
              </p>
              <form onSubmit={handleFeedbackSubmit}>
                <div style={{ 
                  display: 'flex', 
                  gap: '6px', 
                  marginBottom: '4px',
                  padding: '8px 0'
                }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      onClick={() => setRating(star)}
                      style={{
                        cursor: 'pointer',
                        color: star <= rating ? '#FFB400' : '#DFE1E6',
                        fontSize: '28px',
                        transition: 'all 0.15s ease',
                        transform: star <= rating ? 'scale(1.1)' : 'scale(1)',
                        textShadow: star <= rating ? '0 2px 8px rgba(255, 180, 0, 0.3)' : 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (star > rating) {
                          e.target.style.color = '#FFD54F';
                          e.target.style.transform = 'scale(1.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (star > rating) {
                          e.target.style.color = '#DFE1E6';
                          e.target.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      ★
                    </span>
                  ))}
                </div>
              <textarea
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  padding: '8px 12px',
                  marginTop: '10px',
                  borderRadius: '3px',
                  border: '2px solid #dfe1e6',
                  fontSize: '14px',
                  lineHeight: '1.42857143',
                  minHeight: '100px',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontFamily: jiraFont,
                  color: '#172B4D',
                  backgroundColor: '#fafbfc',
                  transition: 'border-color 0.15s ease',
                  outline: 'none',
                }}
                placeholder="Leave a comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = '#4c9aff';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dfe1e6';
                }}
              />
              <button 
                type="submit" 
                style={{
                  ...styles.button,
                  marginTop: '12px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0065FF';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#0052CC';
                }}
              >Submit Feedback</button>
            </form>
            {renderFeedbackHistory()}
          </TabPanel>
              );
            }
            
            return null;
          })}
        </Tabs>
      </>
    );
  };

  // Add a listener for Jira issue updates
  useEffect(() => {
    if (!ap || !ap.events) return undefined;

    const handleIssueUpdate = (event) => {
      setIsAnalysisOutdated(true);
    };

    // Cover all possible event names
    const eventNames = [
      'issue.updated',
      'jiraIssueUpdated',
      'JIRA_ISSUE_UPDATED',
      'ISSUE_UPDATED'
    ];

    eventNames.forEach((name) => {
      try {
        ap.events.on(name, handleIssueUpdate);
      } catch (e) {
        console.warn('Failed to register AP.events listener for', name, e);
      }
    });

      return () => {
      eventNames.forEach((name) => {
        try {
          ap.events.off(name, handleIssueUpdate);
        } catch (e) {
          
        }
      });
    };
  }, [ap]);

  useEffect(() => {
    console.log('State Update:', {
      isLoading,
      error,
      analysisExists,
      isGeneratingAnalysis,
      selectedProject,
      issueId
    });
  }, [isLoading, error, analysisExists, isGeneratingAnalysis, selectedProject, issueId]);

  const checkForUpdates = useCallback(async () => {
    console.log('Checking for updates...', {
      hasAp: !!ap,
      hasContext: !!ap?.context,
      analysisDate,
      issueUpdateTime
    });

    if (!ap?.context) {
      console.log('Missing AP context for update check');
      return;
    }

    // Check if analysisDate is valid (not epoch/invalid date) and if analysis exists
    if (!analysisDate || new Date(analysisDate).getTime() <= 0) {
      // Don't set isAnalysisOutdated to true if analysis doesn't exist yet
      setIsAnalysisOutdated(false);
      return;
    }

    if (!analysisExists) {
      setIsAnalysisOutdated(false);
      return;
    }

    try {
      // Get issue context
      const context = await ap.context.getContext();
      const issueKey = context.jira.issue.key;

      // Get fresh issue update time
      const issueResponse = await ap.request(`/rest/api/3/issue/${issueKey}`);
      const issueData = JSON.parse(issueResponse.body);
      const newUpdateTime = new Date(issueData.fields.updated);
      const existingAnalysisDate = new Date(analysisDate);
      
      if (existingAnalysisDate.getTime() <= 0) {
        setIsAnalysisOutdated(false);
        return;
      }
      
      const isOutdated = existingAnalysisDate < newUpdateTime;
      setIsAnalysisOutdated(isOutdated);
      setIssueUpdateTime(newUpdateTime);

    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }, [ap, analysisDate, analysisExists, isAnalysisOutdated]);

  useEffect(() => {
    if (!isDisplayingProgress || progressQueue.length === 0) return undefined;

    const displayInterval = setInterval(() => {
      setProgressQueue((currentQueue) => {
        if (currentQueue.length === 0) {
          setIsDisplayingProgress(false);
          return currentQueue;
        }

        const [nextCheckpoint, ...remainingQueue] = currentQueue;

        setProgressPercent(nextCheckpoint.percent);
        setProgressMessage(nextCheckpoint.message);
        setLastDisplayedTimestamp(nextCheckpoint.timestamp);
        lastDisplayedTimestampRef.current = nextCheckpoint.timestamp;
        currentProgressPercentRef.current = nextCheckpoint.percent; // Update ref for polling filter
        
        // Save timestamp to localStorage to prevent duplicate display on re-entry
        if (selectedProject && selectedProject.id && issueId) {
          const storageKey = `merlin_last_displayed_${selectedProject.id}_${issueId}`;
          const checkpointTime = toDateValue(nextCheckpoint.timestamp);
          if (checkpointTime) {
            localStorage.setItem(storageKey, checkpointTime.getTime().toString());
          }
        }

        if (nextCheckpoint.percent === 100) {
          setIsDisplayingProgress(false);
          setIsGeneratingAnalysis(false);
          setIsUpdatingAnalysis(false);
          isUpdatingAnalysisRef.current = false;
          setAnalysisExists(true);
          
          // Display pending analysis now that progress reached 100%
          if (pendingAnalysisRef.current) {
            setAnalysis(pendingAnalysisRef.current.analysis);
            const parsedDate = toDateValue(pendingAnalysisRef.current.generatedAt);
            setAnalysisDate(parsedDate ? parsedDate.toISOString() : null);
            pendingAnalysisRef.current = null;
          }
          
          checkForUpdates(); // Trigger Jira check once at completion
          
          // Refresh Jira page to show updated issue title (e.g., "[CodeMerlin]" prefix)
          if (ap && ap.jira && ap.jira.refreshIssuePage) {
            console.log('Refreshing Jira issue page after analysis completion...');
            ap.jira.refreshIssuePage();
          } else {
            console.warn('AP.jira.refreshIssuePage not available at analysis completion');
          }
        }

        return remainingQueue;
      });
    }, 2000);

    return () => clearInterval(displayInterval);
  }, [ap, checkForUpdates, isDisplayingProgress, progressQueue, selectedProject, issueId]);

  const fetchAnalysis = async (projectId, issueKey) => {
    try {
      const token = await ap.context.getToken();
      const response = await fetch(`/get-analysis?projectId=${projectId}&issueId=${issueKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Raw Analysis Result:', result);
      
      // Check for analysis_failed status
      if (result.status === 'analysis_failed') {
        setError(result.errorMessage || 'Analysis failed. Please provide a more detailed description.');
        setAnalysisExists(false);
        setIsGeneratingAnalysis(false);
        setIsUpdatingAnalysis(false);
        isUpdatingAnalysisRef.current = false;
        return { status: 'analysis_failed', error: result.error };
      }
      
      // Only process the result if it has actual analysis content
      if (result && result.analysis && Object.keys(result.analysis).length > 0) {
        setError(null);
        setAnalysisExists(true);
        
        if (result.generatedAt) {
          const parsedDate = toDateValue(result.generatedAt);
          setAnalysisDate(parsedDate ? parsedDate.toISOString() : null);
          console.log('Setting analysis date:', parsedDate ? parsedDate.toISOString() : null);
        }
        
        setAnalysis(result.analysis);
        console.log('Setting analysis log ID:', result.analysisLogId);
        setAnalysisLogId(result.analysisLogId);
        
        return result;
      } else {
        console.log('Received incomplete analysis result, waiting for full data');
        return null;
      }
    } catch (error) {
      console.error('Error fetching analysis:', error);
      setError('An unexpected error occurred');
      setIsGeneratingAnalysis(false);
      setIsUpdatingAnalysis(false);
      isUpdatingAnalysisRef.current = false;
      return null;
    }
  };

  const fetchFeedback = async () => {
    console.log('Fetching feedback for:', { projectId: selectedProject?.id, issueId });
    if (!selectedProject?.id || !issueId) {
      console.error('Missing project ID or issue ID for feedback fetch');
      return;
    }

    try {
      const token = await ap.context.getToken();
      const response = await fetch(`/get-feedback?projectId=${selectedProject.id}&issueId=${issueId}`, {
        headers: {
          'Authorization': `JWT ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch feedback');
      }

      const feedback = await response.json();
      console.log('Received feedback:', feedback);
      setFeedbackHistory(feedback);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  const renderFeedbackHistory = () => (
    <div style={styles.feedbackHistory}>
      <h3 style={styles.subSectionTitle}>Previous Feedback</h3>
      {feedbackHistory.length === 0 ? (
        <p>No feedback submitted yet.</p>
      ) : (
        feedbackHistory.map((feedback, index) => (
          <div key={index} style={styles.feedbackEntry}>
            <div style={styles.feedbackHeader}>
              <div style={styles.stars}>
                {[...Array(5)].map((_, i) => (
                  <span key={i} style={{ 
                    color: i < feedback.rating ? '#FFB400' : '#DFE1E6',
                    fontSize: '16px',
                    textShadow: i < feedback.rating ? '0 1px 4px rgba(255, 180, 0, 0.25)' : 'none',
                  }}>★</span>
                ))}
              </div>
              <span style={styles.feedbackDate}>
                {(() => {
                  const date = new Date(feedback.createdAt);
                  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                })()}
              </span>
            </div>
            {feedback.comment && (
              <div style={styles.feedbackComment}>
                <ReactMarkdown>{feedback.comment}</ReactMarkdown>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  useEffect(() => {
    console.log('Analysis Log ID state changed:', analysisLogId);
  }, [analysisLogId]);

  // Add effect to fetch feedback when tab is active
  useEffect(() => {
    if (activeTab === 5 && selectedProject?.id && issueId) { // 5 is the Feedback tab index (6th tab)
      console.log('Feedback tab is active, fetching feedback...');
      fetchFeedback();
    }
  }, [activeTab, selectedProject?.id, issueId]);

  // Update the date display function to handle invalid dates
  const formatAnalysisDate = (date) => {
    if (!date || new Date(date).getTime() <= 0) {
      return 'Not yet generated';
    }
    const analysisDate = new Date(date);
    return `${analysisDate.getFullYear()}-${String(analysisDate.getMonth() + 1).padStart(2, '0')}-${String(analysisDate.getDate()).padStart(2, '0')} ${String(analysisDate.getHours()).padStart(2, '0')}:${String(analysisDate.getMinutes()).padStart(2, '0')}`;
  };

  // Add the progress tracking effect
  useEffect(() => {
    if (isGeneratingAnalysis) {
      const startTime = Date.now();
      const duration = 60000; // 60 seconds
      
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const newProgress = Math.min((elapsed / duration) * 100, 100);
        setAnalysisProgress(prev => ({ ...prev, analysis: newProgress }));
      }, 1000);

      return () => {
        clearInterval(progressInterval);
        setAnalysisProgress(prev => {
          const next = { ...prev };
          delete next.analysis;
          return next;
        });
      };
    }
  }, [isGeneratingAnalysis]);

  return (
    <div id="merlin-analysis-container" style={styles.container}>
      {renderHeader()}
      
      {/* Show either Analysis or Review based on viewType */}
      {viewType === 'analysis' ? renderContent() : <CommitReview review={analysis?.commitReview} />}
      
      <div style={{
        fontSize: '12px',
        color: '#5e6c84',
        padding: '12px 16px',
        marginTop: '20px',
        textAlign: 'center',
        backgroundColor: '#f4f5f7',
        borderRadius: '3px',
      }}>
        ⚡ CodeMerlin is AI-powered and can make mistakes. All information should be checked and used for guidance, only.
      </div>
      {hasAttachments && analysisExists && analysis && (
        <div style={{
          fontSize: '12px',
          color: '#5e6c84',
          padding: '12px 16px',
          marginTop: '8px',
          textAlign: 'center',
          backgroundColor: '#f4f5f7',
          borderRadius: '3px',
        }}>
          ⚡ <strong>Note:</strong> In the free version of the application, it is not possible to analyze attachments.
        </div>
      )}
    </div>
  );
};

export default MerlinAnalysis;
